import { ButtonText } from "@repo/components/src/ui/button";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import * as Sharing from "expo-sharing";
import { Share } from "lucide-react-native";
import { useCallback } from "react";
import { Gesture } from "react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Zoom from "react-native-zoom-reanimated";
import ModalProvider from "../../../../../globalProviders/modal";
import { Box } from "../../../../../ui/box";
import { Button, ButtonIcon } from "../../../../../ui/button";
import { Image } from "../../../../../ui/image";
import { Pressable } from "../../../../../ui/pressable";

const ANIMATION_DURATION = 400;

const ModalImage = (props: { uri: string }) => {
  const { uri } = props;

  // Shared value para controlar la visibilidad de los menús (1 = visible, 0 = oculto)
  const menuVisible = useSharedValue(true);

  const shareUri = useCallback(async () => {
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(uri);
    }
  }, [uri]);

  // Gesture tap para toggle de menús
  const tapGesture = Gesture.Tap()
    .numberOfTaps(1)
    .onEnd(() => {
      menuVisible.value = !menuVisible.value;
    });

  // Animated style para el header (se desliza hacia arriba)
  const headerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: withTiming(menuVisible.value ? "0%" : "-100%", {
            duration: ANIMATION_DURATION,
          }),
        },
      ],
      opacity: withTiming(menuVisible.value ? 1 : 0, {
        duration: ANIMATION_DURATION,
      }),
    };
  });

  // Animated style para el footer (se desliza hacia abajo)
  const footerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: withTiming(menuVisible.value ? "0%" : "100%", {
            duration: ANIMATION_DURATION,
          }),
        },
      ],
      opacity: withTiming(menuVisible.value ? 1 : 0, {
        duration: ANIMATION_DURATION,
      }),
    };
  });

  return (
    <Box className="flex-1 bg-background-0">
      <Animated.View
        style={headerAnimatedStyle}
        className="absolute top-0 left-0 w-full z-20"
      >
        <HStack
          className="bg-background-0 pt-safe pb-3 px-4 items-center"
          space="md"
        >
          <Heading size="md" className="font-medium flex-1"></Heading>
          <Button
            variant="link"
            action="positive"
            onPress={() => {
              ModalProvider.hide();
            }}
          >
            <ButtonText>Listo</ButtonText>
          </Button>
        </HStack>
      </Animated.View>
      <Pressable
        onPress={() => {
          menuVisible.value = !menuVisible.value;
        }}
        className="flex-1"
      >
        <Zoom>
          <Image
            source={{ uri: uri }}
            size="full"
            contentFit="contain"
            className="rounded-2xl aspect-square bg-black"
            alt="Image"
          />
        </Zoom>
      </Pressable>

      {/* Footer animado */}
      <Animated.View
        style={footerAnimatedStyle}
        className="absolute bottom-0 left-0 w-full z-20"
      >
        <Box className="bg-background-100 pb-safe pt-3">
          <Button
            action="brand"
            variant="link"
            className="h-8 w-full p-0"
            onPress={shareUri}
          >
            <ButtonIcon as={Share} className="h-8 w-8" />
          </Button>
        </Box>
      </Animated.View>
    </Box>
  );
};

export default ModalImage;
